import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { isString } from 'lodash';
import { PATTERN } from 'src/app/constants/pattern';
import { ASSETS_CONSTANTS } from 'src/app/core/constants/Assets-constants';
import {
  ALERT_ERRORS,
  PERSONAL_DATA_VALIDATION,
  PUNTOS_COLOMBIA_VALIDATIONS,
} from 'src/app/core/constants/Errors';
import { DOC_TYPES } from 'src/app/core/constants/FormLists';
import {
  ALERT_ICON_CONSTANTS,
  GENERAL_CONSTANTS,
} from 'src/app/core/constants/General-constants';
import { AUTOCOMPLETE_METHOD_PAY_TYPES } from 'src/app/core/constants/General-constants';
import { Payment, Transaction } from 'src/app/models';
import { AlertService } from 'src/app/services/alert.service';
import { PatchValAdditionalInfoService } from 'src/app/services/patch-val-additional-info.service';
import { PuntosColombiaService } from 'src/app/services/puntos-colombia.service';
import { TokenServiceService } from 'src/app/services/token-service.service';
import { EmailValidator } from 'src/app/tools/email-validator';
import { SweetAlertIcon } from 'sweetalert2';

export interface PcoTransactionRequest {
  userInformation: {
    name: string;
    documentType: string;
    documentNumber: string;
    cellphone: string;
    email: string;
  };
}

@Component({
  selector: 'app-puntos-colombia',
  templateUrl: './puntos-colombia.component.html',
})
export class PuntosColombiaComponent implements OnInit {
  @Input() information: Payment;
  @Input() transaction: Transaction;
  @Output() PCOL = new EventEmitter();

  form: FormGroup;
  validate_document: boolean;
  formPrivacyValid: boolean;
  loading: boolean;
  pcoTransaction: any;
  errorMessages = PERSONAL_DATA_VALIDATION;
  listOfDocuments = [];
  errorsMessages = PUNTOS_COLOMBIA_VALIDATIONS;
  logo_PCO = ASSETS_CONSTANTS.PUNTOS_COLOMBIA_LOGO;
  isEditableInfo: boolean;
  template: string;

  objectControlData = [
    {
      formControl: 'name',
      readOnly: false,
    },
    {
      formControl: 'lastname',
      readOnly: false,
    },
    {
      formControl: 'typeDI',
      readOnly: false,
    },
    {
      formControl: 'di',
      readOnly: false,
    },
    {
      formControl: 'cellPhone',
      readOnly: false,
    },
    {
      formControl: 'email',
      readOnly: false,
    },
  ];
  token: any;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly alertService: AlertService,
    private readonly tokenService: TokenServiceService,
    private readonly patchValAdditionalInfoService: PatchValAdditionalInfoService,
    private readonly pcolService: PuntosColombiaService
  ) {
    this.form = this.formBuilder.group({
      typeDI: ['', Validators.required],
      di: [
        '',
        Validators.compose([
          Validators.required,
          Validators.maxLength(25),
          Validators.minLength(6),
          Validators.pattern(PATTERN.NUMBERS),
        ]),
      ],
      cellPhone: [
        '',
        Validators.compose([
          Validators.required,
          Validators.maxLength(10),
          Validators.minLength(10),
          Validators.pattern(PATTERN.NUMBERS),
        ]),
      ],
      email: [
        '',
        Validators.compose([
          Validators.required,
          EmailValidator.isValid,
          Validators.maxLength(150),
        ]),
      ],
      name: [
        '',
        Validators.compose([
          Validators.required,
        ]),
      ],
      lastName: [
        '',
        Validators.compose([
          Validators.required,
        ]),
      ],
    });

    const docTypesObjectKeys = Object.keys(DOC_TYPES);

    for (let prop of docTypesObjectKeys) {
      this.listOfDocuments.push(DOC_TYPES[prop]);
    }
  }

  ngOnInit() {
    this.token = this.tokenService.getToken();
    this.template = this.information.template;
    this.disabledItems();
  }

  /**
   * @description {formPrivacyUpdate} Function that obtains the event of the validation of the child component
   * @param valid
   */
  formPrivacyUpdate = (valid: boolean) => {
    this.formPrivacyValid = valid;
  };

  /**
  * @description {completedForms} Function to validate the validations of the parent component and child component
  * @returns
  */
  completedForms = (): boolean => {
    return this.form.valid && this.formPrivacyValid;
  };

  /**
   * @description Asynchronously validates the data in the form, updating the 'loading' flag accordingly.
   * Converts the 'di' property of 'puntosColombiaTransaction' to a string.
   */
  async validData() {
    this.loading = true;
    this.pcoTransaction = this.form.value;
    const fullName = `${this.pcoTransaction.name} ${this.pcoTransaction.lastName}`;
    this.pcoTransaction.di = this.pcoTransaction.di.toString();

    const pcoTransactionRequest: PcoTransactionRequest = {
      userInformation: {
        name: fullName,
        documentType: this.pcoTransaction.typeDI,
        documentNumber: this.pcoTransaction.di,
        cellphone: this.pcoTransaction.cellPhone,
        email: this.pcoTransaction.email,
      },
    };

    if (this.completedForms()) {
      return await this.puntosColombiaRegister(pcoTransactionRequest);
    }

    this.loading = false;

    this.alertService.showAlert(
      ALERT_ERRORS.DATA_IMCOMPLETED,
      ALERT_ICON_CONSTANTS.WARNING as SweetAlertIcon
    );
  }

 /**
   * Registers a transaction request for puntos Colombia.
   *
   * @param {PcoTransactionRequest} pColRequest - The transaction request object.
   * @return {Promise<void>} - A promise that resolves when the registration is complete.
   */
  async puntosColombiaRegister(pColRequest: PcoTransactionRequest) {
    try {
      await this.pcolService
        .puntosColombiaRegister(pColRequest, this.token)
        .then((res: any) => {
          this.loading = false;

          if (res.CODE !== GENERAL_CONSTANTS.OK_RESPONSE_CODE) {
            let errorMsg =
              res.DESC != null && isString(res.DESC)
                ? res.DESC
                : ALERT_ERRORS.UNEXPECTED_ERROR;

            return this.alertService.showAlert(
              errorMsg,
              ALERT_ICON_CONSTANTS.ERROR as SweetAlertIcon
            );
          }

          return (window.location.href = res.DATA.callbackUrl);
        })
        .catch((_) => {
          this.loading = false;

          return this.alertService.showAlert(
            ALERT_ERRORS.UNEXPECTED_ERROR,
            ALERT_ICON_CONSTANTS.ERROR as SweetAlertIcon
          );
        });
    } catch (_) {
      this.loading = false;

      this.alertService.showAlert(
        ALERT_ERRORS.UNEXPECTED_ERROR,
        ALERT_ICON_CONSTANTS.ERROR as SweetAlertIcon
      );
    }
  }

  /**
  * @description Validates the format of the document ID in the form and
  * sets the 'validate_document' flag accordingly.
  */
  validDI() {
    if (this.form.value.di.includes('-')) {
      if (!PATTERN.VALID_DI.test(this.form.value.di)) return this.validate_document = true;

      this.validate_document = false;
    }
  }

  /**
  *
  * @description {disabledItems} Function to disabled items to checkout form
  */
  disabledItems() {
    try {
      let additionalInfo = this.tokenService.exitsAdditionalInformation( this.transaction );

      if (additionalInfo) {
        this.isEditableInfo = this.tokenService.isEditableInfo(additionalInfo);

        return this.patchValAdditionalInfoService.patchValAdditionalInfo(
          this.isEditableInfo,
          additionalInfo,
          this.form,
          AUTOCOMPLETE_METHOD_PAY_TYPES.PUNTOS_COLOMBIA,
          this.objectControlData
        );
      }
    } catch (_) {
      this.alertService.showAlert(ALERT_ERRORS.GET_TOKEN_DATA_ERROR);
    }
  }

  /**
   * @description {isControlReadOnly} function to check if form fields are read-only
   * @param controlName
   * @returns
   */
  isControlReadOnly(controlName: string): boolean {
    const readOnlyControl = this.objectControlData.find((m) => m.formControl === controlName);

    return readOnlyControl ? readOnlyControl.readOnly : false;
  }

  /**
   * @description changes the pay method and emits a PCOL event.
   * @returns
   */
  changeMethod() {
    this.PCOL.emit();
  }
}
